// frontend/src/admin/GDPRWipePanel.jsx
import { useState } from 'react'
import axios from 'axios'
import { Box, Button, TextField, Typography, Alert, Stack, CircularProgress } from '@mui/material'
import PageContainer from '../components/common/PageContainer'
import DisabledReason from '../components/common/DisabledReason'
import { HoldButton } from '../components/common/confirm'
import ViewHeader from '../components/ViewHeader'
import { FS, SP, PANEL_SX } from '../theme/tokens'
import { toast } from 'sonner'

const API = process.env.REACT_APP_API_BASE_URL

export default function GDPRWipePanel() {
  const [email, setEmail] = useState('')
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [executing, setExecuting] = useState(false)

  const runPreview = async () => {
    if (!email.trim()) return
    setLoading(true)
    setPreview(null)
    try {
      const res = await axios.get(`${API}/api/admin/notify/gdpr/preview`, { params: { email: email.trim() } })
      setPreview(res.data)
    } catch (e) {
      toast.error('Preview failed')
    } finally {
      setLoading(false)
    }
  }

  const runExecute = async () => {
    if (!preview) return
    // Bestaetigung liegt am HoldButton darunter. Die Zahlen standen ohnehin
    // schon in der Alert-Zeile ueber dem Knopf.
    //
    // 🔴 W9 der Gates zu PR 4: der entfernte `window.confirm` sagte zwei Dinge,
    // die die Zahlen NICHT sagen — „This is irreversible." und die ADRESSE. Die
    // Unwiderruflichkeit war die eigentliche Warnung vor einem HoldButton; die
    // Adresse ist der Schutz gegen einen veralteten Vorschaustand (der Nutzer
    // kann sie gegen das Feld darueber halten). Beides steht jetzt in der
    // Alert-Zeile.
    setExecuting(true)
    try {
      const res = await axios.post(`${API}/api/admin/notify/gdpr/execute`, { email: email.trim(), confirm: true })
      toast.success(`Redacted ${res.data.notifications_redacted} notifications + ${res.data.email_log_redacted} email log rows`)
      setPreview(null)
      setEmail('')
    } catch (e) {
      toast.error('Execute failed')
    } finally {
      setExecuting(false)
    }
  }

  return (
    <PageContainer>
      <ViewHeader title="GDPR — Notification Wipe" breadcrumbs={[{ label: 'Admin' }, { label: 'GDPR Wipe' }]} />
      <Box sx={{ ...PANEL_SX, p: SP.section }}>
        <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', mb: SP.sm }}>
          Redact PII (recipient_email, title, body) for the given email address. Audit keys (id, trigger_key, timestamps) are preserved. Preview first; execute requires explicit confirm.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: SP.sm, mb: SP.section }}>
          <TextField
            size="small"
            label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ minWidth: 320 }}
            type="email"
          />
          <DisabledReason
            reason={loading
              ? null
              : !email.trim() ? 'Enter an email address first.' : null}
          >
            <Button variant="outlined" onClick={runPreview} disabled={!email.trim() || loading}>
              {loading ? <CircularProgress size={16} /> : 'Preview'}
            </Button>
          </DisabledReason>
        </Stack>
        {preview && (
          <Alert severity="warning" sx={{ mb: SP.section }}>
            Will redact: <strong>{preview.notifications_to_redact}</strong> notifications + <strong>{preview.email_log_to_redact}</strong> email log rows
            {' '}for <strong>{email.trim()}</strong>. This is irreversible.
            <Box sx={{ mt: 1 }}>
              {/* Zerstoerend ohne Rueckweg -> Halten statt Klicken (Pattern C).
                  🔴 R16-B F-3: `HoldButton` warnt in seinem `@dontUseFor` vor
                  „accessibility-critical paths where motor-impaired users must
                  succeed (provide a fallback tap-to-confirm escape hatch)".
                  Hier ist die Entscheidung bewusst: der Vorgang ist
                  unwiderruflich und Admin-only, die Haltegeste ist der einzige
                  Schutz gegen einen Fehlklick, und die Tastatur traegt denselben
                  Weg (Space/Enter gehalten). Ein zweiter, leichterer
                  Bestaetigungsweg waere eine zweite Tuer an derselben Wand —
                  er wird nicht gebaut, sondern benannt. */}
              <HoldButton
                holdMs={900}
                label="Hold to execute wipe"
                labelActive="Keep holding…"
                labelDone="Wiped"
                color="error"
                disabled={executing}
                onConfirm={runExecute}
              />
            </Box>
          </Alert>
        )}
      </Box>
    </PageContainer>
  )
}
